import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IFileAttachment, SocialMediaPostViewModel } from '../../../../extViewmodels';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { AddMediaEmptyState } from '../../../components-v2/AddMediaEmptyState';
import { UnifiedImageDialog } from '../../../components-v2/UnifiedImageDialog';
import { TabValue } from '../../../components-v2/UnifiedImageDialog/types';
import { useEditSocialMediaPostContext } from '../../../containers/socialMedia/EditSocialMediaPost/context';
import { IMediaRouting, MediaGrid } from './MediaGrid';
import { styleSheet } from './styles';

export type { IMediaRouting } from './MediaGrid';

/** The media actions the composer drives from the version bar above the panel. */
export interface IMediaActions {
	openBulkRebranding: () => void;
}

interface IProps extends IImpersonationContextComponentProps {
	/** Filled with the actions the composer renders outside this component. */
	actionsRef?: React.Ref<IMediaActions>;
	/** Heading for the whole media section. */
	label: string;
	onPostScheduled?: (postViewModel: SocialMediaPostViewModel) => void;
	onValidateSelectedFile?: (file: File) => boolean;
	post?: SocialMediaPostViewModel;
	hasVideo: boolean;
	readOnly?: boolean;
	/** Set while the post is split, so the grid routes media to the version on screen as well. */
	routing?: IMediaRouting;
	styles?: StyleDeclarationValue[];
}

export const MAX_IMAGES = 10;

/** The dialog tabs that bring new media into the post, rather than changing media already in it. */
const ADDING_TABS: readonly TabValue[] = ['upload', 'image-library', 'ai-generate', 'free-images', 'upload-video'];

const isVideoAttachment = (attachment: IFileAttachment) => !!attachment.mimeType?.toLowerCase().startsWith('video/');

/** A post publishes one video on its own or up to MAX_IMAGES images, and every platform rejects the mix. */
const getAddBlockedReason = (hasVideo: boolean, mediaCount: number) => {
	if (hasVideo) {
		return 'A video publishes on its own. Remove it to add other media.';
	}
	return mediaCount >= MAX_IMAGES ? `Only ${MAX_IMAGES} images are supported at this time` : undefined;
};

/** Checked against the pool as it is when the addition lands, not as it was when the dialog opened. */
const appendMedia = (pool: IFileAttachment[], additions: IFileAttachment[]) =>
	getAddBlockedReason(pool.some(isVideoAttachment), pool.length) ? pool : [...pool, ...additions].slice(0, MAX_IMAGES);

interface IMediaRules {
	/** Why nothing more can be added right now, or undefined while it can. */
	addBlockedReason?: string;
	/** False on a read-only post, which shows its media but does not own it. */
	canChange: boolean;
	editWithAIBlockedReason?: string;
	/** Whether the grid offers a way to add at all. A post holding a video offers none. */
	offersAdd: boolean;
	rebrandBlockedReason?: string;
}

/** What may be done to the post's media, kept in one place so the grid and its dialog always agree. */
const getMediaRules = ({
	hasVideo,
	isCustomized,
	mediaCount,
	readOnly,
}: {
	hasVideo: boolean;
	isCustomized: boolean;
	mediaCount: number;
	readOnly: boolean;
}): IMediaRules => ({
	addBlockedReason: getAddBlockedReason(hasVideo, mediaCount),
	canChange: !readOnly,
	// An AI edit and a rebrand hand back new attachments with new ids. Versions route media by id, so
	// while the post is customized, targeted platforms or not, each would lose the media it routed.
	editWithAIBlockedReason: isCustomized ? 'Turn off Customize by platform to edit this media with AI' : undefined,
	offersAdd: !readOnly && !hasVideo,
	rebrandBlockedReason: isCustomized
		? 'Turn off Customize by platform to apply your branding to this media'
		: undefined,
});

const getDisabledDialogTabs = (rules: IMediaRules, videoUploadBlockedReason: string | undefined) => {
	const disabledTabsReason: Partial<Record<TabValue, string>> = {};
	if (rules.addBlockedReason) {
		ADDING_TABS.forEach(tab => {
			disabledTabsReason[tab] = rules.addBlockedReason;
		});
	} else if (videoUploadBlockedReason) {
		disabledTabsReason['upload-video'] = videoUploadBlockedReason;
	}
	if (rules.rebrandBlockedReason) {
		disabledTabsReason['bulk-rebranding'] = rules.rebrandBlockedReason;
	}
	return { disabledTabs: Object.keys(disabledTabsReason) as TabValue[], disabledTabsReason };
};

const SocialMediaImageSearchAndDisplayBase: React.FC<IProps> = observer(props => {
	const {
		actionsRef,
		label,
		readOnly,
		hasVideo,
		onPostScheduled,
		onValidateSelectedFile,
		post,
		routing,
		impersonationContext,
	} = props;
	const {
		customizeByPlatform,
		hideWhiteBackground,
		logoSize,
		overlayLogo,
		postImages,
		postContent,
		replaceImage,
		schedulePost,
		selectedCreator,
		setHasChanges,
		setImages,
		setLogoSize,
		setOverlayLogo,
		updatePost,
	} = useEditSocialMediaPostContext();
	const userSession = useUserSession();
	const account = impersonationContext?.account || userSession?.account;
	const [unifiedDialogOpen, setUnifiedDialogOpen] = React.useState(false);
	const [unifiedDialogTab, setUnifiedDialogTab] = React.useState<TabValue>('upload');
	const [unifiedDialogImage, setUnifiedDialogImage] = React.useState<IFileAttachment | null>(null);
	const [logoOverlayImageId, setLogoOverlayImageId] = React.useState<string | undefined>(undefined);
	const [cropImageId, setCropImageId] = React.useState<string | undefined>(undefined);
	const rules = getMediaRules({
		hasVideo,
		isCustomized: !!customizeByPlatform,
		mediaCount: postImages?.length ?? 0,
		readOnly: !!readOnly,
	});

	const onRemoveClicked = React.useCallback(
		(image: IFileAttachment) => {
			setImages(val => val?.filter(x => x.id !== image.id));
		},
		[setImages]
	);

	const handleOpenImageModal = React.useCallback((tab: TabValue = 'upload') => {
		setUnifiedDialogTab(tab);
		setUnifiedDialogImage(null);
		setUnifiedDialogOpen(true);
	}, []);

	// Opening on a disabled tab would still show its content, so a blocked rebrand opens nothing.
	const canRebrand = rules.canChange && !rules.rebrandBlockedReason;
	React.useImperativeHandle(
		actionsRef,
		() => ({
			openBulkRebranding: () => {
				if (canRebrand) {
					handleOpenImageModal('bulk-rebranding');
				}
			},
		}),
		[canRebrand, handleOpenImageModal]
	);

	const handleOpenAIEdit = React.useCallback((image: IFileAttachment) => {
		setUnifiedDialogTab('ai-edit');
		setUnifiedDialogImage(image);
		setUnifiedDialogOpen(true);
	}, []);

	// Enlarging opens the same tab that owns the logo overlay: one place shows the media at size, and
	// what the logo will do to it.
	const handleEnlarge = React.useCallback((image: IFileAttachment) => {
		setUnifiedDialogTab('logo-overlay');
		setUnifiedDialogImage(null);
		setLogoOverlayImageId(image.id);
		setUnifiedDialogOpen(true);
	}, []);

	const handleOpenCrop = React.useCallback((image: IFileAttachment) => {
		setUnifiedDialogTab('crop');
		setUnifiedDialogImage(null);
		setCropImageId(image.id);
		setUnifiedDialogOpen(true);
	}, []);

	const handleUnifiedImageSelected = React.useCallback(
		(image: IFileAttachment) => {
			setImages(prev => {
				// If we're editing an existing image, replace it instead of adding a new one
				if (unifiedDialogImage) {
					return (prev || []).map(img => (img.id === unifiedDialogImage.id ? image : img));
				}
				return appendMedia(prev || [], [image]);
			});
		},
		[setImages, unifiedDialogImage]
	);

	const handleMultipleImagesSelected = React.useCallback(
		(images: Api.IFileAttachment[]) => {
			setImages(prev => appendMedia(prev || [], images));
		},
		[setImages]
	);

	// A crop replaces one picture rather than adding one, and the upload gives it a new id. Routed
	// through setImages that new id reads as an addition, which would drop the original from every
	// platform version and hand the crop to one — so the swap goes through replaceImage instead.
	const handleCropped = React.useCallback(
		(original: IFileAttachment, cropped: IFileAttachment) => {
			replaceImage(original.id, cropped);
			setCropImageId(cropped.id);
			setUnifiedDialogOpen(false);
		},
		[replaceImage]
	);

	const handleBulkRebrandingImagesSelected = (attachments: Api.IFileAttachment[]) => {
		setUnifiedDialogOpen(false);
		setImages(attachments);
	};

	const handleVideoAttachmentGenerated = React.useCallback(
		(attachment: IFileAttachment) => {
			setImages(prev => appendMedia(prev || [], [attachment]));
			setUnifiedDialogOpen(false);
		},
		[setImages]
	);

	const getPostIdForVideoUpload = React.useCallback(async (): Promise<string> => {
		if (post?.id) {
			await updatePost(post);
			return post.id;
		}
		const [postViewModel] = await schedulePost(null, null, impersonationContext);
		onPostScheduled?.(postViewModel);
		return postViewModel.id;
	}, [onPostScheduled, post, schedulePost, updatePost, impersonationContext]);

	const videoForUserId = React.useMemo(() => {
		if (impersonationContext?.isValid && !selectedCreator) {
			return undefined;
		}

		if (impersonationContext?.isValid) {
			return selectedCreator?.id;
		}

		if (selectedCreator?.id && selectedCreator.id !== userSession?.user?.id) {
			return selectedCreator.id;
		}

		return undefined;
	}, [impersonationContext?.isValid, selectedCreator, userSession?.user?.id]);

	const disabledDialogTabs = getDisabledDialogTabs(
		rules,
		impersonationContext?.isValid && !videoForUserId ? 'You must select a creator to upload a video' : undefined
	);

	// The overlay is burned in at publish time, so it is settled only while no image already carries
	// one. Offered from here because this dialog is where the media it lands on gets chosen.
	const logoOverlay = {
		hideWhiteBackground,
		images: postImages ?? [],
		initialImageId: logoOverlayImageId,
		isBurnedIn: !!postImages?.some(image => image.hasLogoOverlay),
		isEnabled: !!overlayLogo,
		logoUrl: account?.preferences?.logo?.url,
		onEnabledChange: (isEnabled: boolean) => {
			setOverlayLogo(isEnabled);
			setHasChanges(true);
		},
		onSizeChange: setLogoSize,
		readOnly: !rules.canChange,
		size: logoSize ?? Api.DEFAULT_LOGO_SIZE,
	};

	const crop = rules.canChange
		? {
				images: postImages ?? [],
				initialImageId: cropImageId,
				onCropped: handleCropped,
			}
		: undefined;

	const hasMedia = !!postImages?.length;

	return (
		<>
			<div className={css(styleSheet.mediaHeader)}>
				<label className={css(styleSheet.mediaLabel)}>{label}</label>
			</div>
			{hasMedia ? (
				<MediaGrid
					addMediaDisabledReason={rules.addBlockedReason}
					attachments={postImages}
					editWithAIDisabledReason={rules.editWithAIBlockedReason}
					label={routing ? `Post media, routing to ${routing.platform}` : 'Post media'}
					onAddMedia={rules.offersAdd ? () => handleOpenImageModal('upload') : undefined}
					onCrop={rules.canChange ? handleOpenCrop : undefined}
					onEditWithAI={rules.canChange ? handleOpenAIEdit : undefined}
					onEnlarge={handleEnlarge}
					onRemove={rules.canChange ? onRemoveClicked : undefined}
					onReorder={setImages}
					readOnly={readOnly}
					routing={routing}
				/>
			) : rules.offersAdd ? (
				<AddMediaEmptyState
					title='No media selected'
					subtitle='Add images or videos to display with your post'
					onAddMedia={() => handleOpenImageModal('upload')}
				/>
			) : null}

			<UnifiedImageDialog
				allowAIEdit={rules.canChange}
				showAIGenerateTab={rules.canChange}
				showFreeImagesTab={rules.canChange}
				showImageLibraryTab={rules.canChange}
				offerSaveToLibrary
				open={unifiedDialogOpen}
				onOpenChange={setUnifiedDialogOpen}
				initialTab={unifiedDialogTab}
				initialImage={unifiedDialogImage || undefined}
				onImageSelected={handleUnifiedImageSelected}
				onVideoAttachmentGenerated={handleVideoAttachmentGenerated}
				impersonationContext={impersonationContext}
				destinationPlatform={routing?.platform}
				logoOverlay={logoOverlay}
				crop={crop}
				postContent={postContent}
				postId={post?.id}
				getPostIdForVideoUpload={getPostIdForVideoUpload}
				forUserId={videoForUserId}
				showUploadTab={rules.canChange}
				showUploadVideoTab={rules.canChange}
				resizeForSocialMedia={true}
				onValidateSelectedFile={onValidateSelectedFile}
				bulkRebrandingImages={rules.canChange ? postImages : undefined}
				onBulkRebrandingImagesSelected={handleBulkRebrandingImagesSelected}
				maxUploadImages={Math.max(MAX_IMAGES - (postImages?.length ?? 0), 0)}
				allowMultipleUpload={true}
				onMultipleImagesSelected={handleMultipleImagesSelected}
				disabledTabs={disabledDialogTabs.disabledTabs}
				disabledTabsReason={disabledDialogTabs.disabledTabsReason}
			/>
		</>
	);
});

export const SocialMediaImageSearchAndDisplay = inject(ImpersonationContextKey)(SocialMediaImageSearchAndDisplayBase);
